import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiCalendarLight,
  PiPencilSimpleLineLight,
  PiFileLight,
  PiHardDrivesLight,
  PiUserLight,
  PiBriefcaseLight,
} from 'react-icons/pi';
import { FieldGrid, MetaTile, InfoRow } from '../../shared/brand/Field';
import GlassCard from '../../shared/brand/GlassCard';
import CollaboratorStack from '../../shared/access/CollaboratorStack';
import { formatBytes, formatDateTime } from '../../../utils/functions';

export interface DocumentInfoPerson {
  user_uuid: string;
  name: string;
  initials: string;
  access: string;
  email?: string | null;
  avatar_url?: string | null;
}

export interface DocumentInfo {
  name: string;
  type?: string;
  size?: number;
  createdAt?: string;
  modifiedAt?: string;
  uploadedBy?: string;
  matterTitle?: string;
  folderPath?: string;
  sharedWith?: DocumentInfoPerson[];
}

/**
 * The document's metadata, on demand (files-3).
 *
 * The viewport belongs to the document: the name lives in the breadcrumb and
 * nowhere else, and everything that used to crowd the navigator row - type,
 * size, created, modified - is here behind the header's Info button.
 */
const DocumentInfoModal: React.FC<{ info: DocumentInfo }> = ({ info }) => {
  const { t } = useTranslation();
  const shared = info.sharedWith ?? [];

  return (
    <div className="px-4 py-2 space-y-4 min-w-[420px] max-w-[560px]">
      <FieldGrid columns={2}>
        <MetaTile icon={<PiFileLight />} label={t('Type')} value={info.type} />
        <MetaTile
          icon={<PiHardDrivesLight />}
          label={t('Size')}
          value={info.size != null && info.size > 0 ? formatBytes(info.size) : undefined}
        />
        <MetaTile
          icon={<PiCalendarLight />}
          label={t('Uploaded / created')}
          value={info.createdAt ? formatDateTime(info.createdAt) : undefined}
        />
        <MetaTile
          icon={<PiPencilSimpleLineLight />}
          label={t('Last modified')}
          value={info.modifiedAt ? formatDateTime(info.modifiedAt) : undefined}
        />
      </FieldGrid>

      <GlassCard title={t('Details')} padding="md">
        <div className="space-y-2">
          <InfoRow label={t('File name')} value={info.name} />
          {info.folderPath && <InfoRow label={t('Location')} value={info.folderPath} />}
          {info.uploadedBy && (
            <InfoRow label={t('Uploaded by')}>
              <span className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200 truncate">
                <PiUserLight className="w-3.5 h-3.5 text-fg-muted" />
                {info.uploadedBy}
              </span>
            </InfoRow>
          )}
          {info.matterTitle && (
            <InfoRow label={t('Matter')}>
              <span className="flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-200 truncate">
                <PiBriefcaseLight className="w-3.5 h-3.5 text-fg-muted" />
                {info.matterTitle}
              </span>
            </InfoRow>
          )}
          <InfoRow label={t('Shared with')}>
            {shared.length > 0 ? (
              <CollaboratorStack collaborators={shared} max={6} size="sm" />
            ) : (
              <span className="text-sm text-gray-400 dark:text-gray-500">{t('Not shared')}</span>
            )}
          </InfoRow>
        </div>
      </GlassCard>
    </div>
  );
};

export default DocumentInfoModal;
